import { observer } from "mobx-react-lite"

import { TokenStore } from "@/domains/tokens"

/**
 * Overlapping token-logo strip used anywhere we need to render a pool pair,
 * basket, or quick-select triplet. Accepts `TokenStore` instances so callers
 * can pass stores directly (e.g. `pool.token0`/`pool.token1`) without
 * projecting down to primitive DTOs.
 *
 * Wrapped in `observer` so that if a token's observable metadata
 * (`symbol`, `imageURL`, `chainSlug`) ever mutates — for instance when the
 * token-api repository patches a previously-unknown logo — the icons update
 * without relying on the parent's re-render to drag us along.
 */
export const DisplayTokens = observer(function DisplayTokens({
  tokens,
  gap = 2,
  tokenSize = 36,
  overlap = 6,
}: {
  tokens: Array<TokenStore>
  gap?: number
  tokenSize?: number
  overlap?: number
}) {
  const tokensPerRow = tokens.length
  const containerWidth = tokenSize * tokensPerRow + gap * (tokensPerRow - 1) - overlap * (tokensPerRow - 1)
  return (
    <div
      className={"relative flex"}
      style={{
        height: `${tokenSize}px`,
        minWidth: `${containerWidth}px`,
        maxWidth: `${containerWidth}px`,
        width: `${containerWidth}px`,
      }}
    >
      {tokens.map((token, index) => {
        return (
          <img
            key={token.address.toString()}
            className="absolute top-0 aspect-square"
            style={{
              left: `${index * (tokenSize + gap - overlap)}px`,
              zIndex: tokensPerRow - index,
            }}
            src={token.imageURL}
            width={tokenSize}
            height={tokenSize}
            alt={token.symbol}
            onError={(error: React.SyntheticEvent<HTMLImageElement, Event>) => {
              if (error) {
                error.currentTarget.onerror = null
                console.log("DisplayTokens", token.chainSlug)

                error.currentTarget.src = `/assets/${token.chainSlug}/unknown-logo.png`
              }
            }}
          />
        )
      })}
    </div>
  )
})
